/**
 * FlahaSOIL — frontend password policy mirror.
 *
 * Mirrors `backend/src/auth/password.ts` so the SPA can surface the
 * same constraints in form-level validation without a round-trip. The
 * backend remains the source of truth; if these constants diverge the
 * server still wins (the SPA just shows a less helpful error).
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** Keep in sync with backend/src/auth/commonPasswords.ts (subset for UX). */
const COMMON_PASSWORDS = new Set([
	"password1234",
	"password12345",
	"password123456",
	"qwertyuiop12",
	"qwerty123456",
	"welcome12345",
	"admin1234567",
	"letmein12345",
	"changeme1234",
	"flahasoil123",
	"flahasoil1234",
	"p@ssw0rd1234",
	"p@ssword1234",
	"1234567890ab",
	"1q2w3e4r5t6y",
]);

export interface PasswordPolicyError {
	message: string;
}

export function validatePassword(password: string): PasswordPolicyError | null {
	if (password.length < PASSWORD_MIN_LENGTH) {
		return {
			message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
		};
	}
	if (password.length > PASSWORD_MAX_LENGTH) {
		return {
			message: `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`,
		};
	}
	if (!/[A-Za-z]/.test(password)) {
		return { message: "Password must contain at least one letter." };
	}
	if (!/[0-9]/.test(password)) {
		return { message: "Password must contain at least one digit." };
	}
	if (COMMON_PASSWORDS.has(password.toLowerCase())) {
		return {
			message: "Password is too common; choose a less predictable value.",
		};
	}
	return null;
}
